import { IdName, SelectedItemsObj } from '@components/multi-selector/types';
import { isInvalidMemberName, isInvalidPhoneNumber } from '@utils/validations';
import { DropdownOption } from 'types/dropdown';

import { RoleDetails, RoleType } from './constants';

export const getMemberCount = (count: number) =>
  count && (count < 10 ? `0${count}` : count);

export const getRoleTitleAndDescription = (
  role: RoleType,
  type: 'title' | 'description'
) => {
  if (type === 'title') return RoleDetails[role].title;
  return RoleDetails[role].description;
};

export const getSelectedBranches = (
  branches: { branch_id: string; branch_name: string }[]
) =>
  branches.map((branch) => ({
    id: branch.branch_id,
    label: branch.branch_name,
    value: branch.branch_name
  }));

interface BranchGroups {
  [key: string]: IdName[];
}

interface GroupList {
  group_id: string;
  group_name: string;
}

export const getSelectedBranchGroups = (branches: any) => {
  const val = branches?.reduce(
    (
      acc: BranchGroups,
      curr: {
        branch_id: string | number;
        group_list: GroupList[];
      }
    ) => {
      acc[curr.branch_id] = curr?.group_list?.map((group: GroupList) => ({
        id: group.group_id,
        name: group.group_name
      }));
      return acc;
    },
    {}
  );
  return val;
};

type GroupListItem = {
  id?: string;
  name?: string;
  group_id?: string;
  group_name?: string;
};

export const extractGroupList = (data: unknown): GroupListItem[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'data' in data) {
    const nested = (data as { data: unknown }).data;
    if (Array.isArray(nested)) return nested;
  }
  return [];
};

export const formatBranchGroupsData = (data: unknown): IdName[] =>
  extractGroupList(data)
    .map((item) => ({
      id: String(item.id ?? item.group_id ?? ''),
      name: String(item.name ?? item.group_name ?? '')
    }))
    .filter((item) => item.id && item.name);

export const isNameAndPhoneValid = (
  name: string,
  phoneNumber: string,
  nameMaxLength: string,
  nameMinLength: string
) =>
  isInvalidMemberName(name, nameMaxLength, nameMinLength) ||
  isInvalidPhoneNumber(phoneNumber);

export const hasChangeInEditRole = (
  selectedRole: RoleType,
  role_name: string,
  branchOptions: DropdownOption[],
  branch_list: any,
  selectedGroups: SelectedItemsObj
) => {
  const selectedBranchIds = branchOptions.map((branch) => branch.id).sort();
  const branchIds = getSelectedBranches(branch_list || [])
    .map((branch) => branch.id)
    .sort();

  const selectedGroupIds: string[] = [];
  Object.values(selectedGroups).forEach((item) =>
    selectedGroupIds.push(...item.map((group) => group.id))
  );
  selectedGroupIds.sort();

  const groupIds: any[] = [];
  Object.values(
    getSelectedBranchGroups(branch_list || []) as SelectedItemsObj
  ).forEach((item) => groupIds.push(...item.map((group) => group.id)));
  groupIds.sort();

  return (
    selectedRole !== role_name ||
    ([
      RoleType.BRANCH_ADMIN,
      RoleType.FINANCE_ADMIN,
      RoleType.EVENT_MANAGER
    ].includes(selectedRole) &&
      JSON.stringify(branchIds) !== JSON.stringify(selectedBranchIds)) ||
    ([
      RoleType.GROUP_ADMIN,
      RoleType.ATTENDANCE_MANAGER,
      RoleType.PAYMENT_MANAGER
    ].includes(selectedRole) &&
      JSON.stringify(selectedGroupIds) !== JSON.stringify(groupIds))
  );
};
